import { useEffect, useRef, useState } from "react";
import { Button, Field, Picker, DialogCancel } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCreateAsset, useUpdateAsset, useDeleteAsset } from "../../app/hooks/useRelease.js";
import { useUploadMedia, mediaKindForFile } from "../../app/hooks/useMedia.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { Modal } from "./Modal.js";
import { MediaImage } from "./MediaImage.js";
import { UploadField } from "./UploadField.js";
import styles from "./AssetEditorModal.module.scss";

type AssetDto = components["schemas"]["AssetDto"];

// The asset editor modal (shot-10) — add/edit one asset-library tile, reusing the release Modal primitive
// (focus-trap/Escape/scroll-lock). It adapts by kind: cover/photo/canvas upload a file via the flow
// (real progress), music/lyric video paste a URL, lyrics is inline text. A file upload runs on Save and its
// failure surfaces INLINE — the asset is never persisted (and never reports success) unless its bytes landed
// The per-row asset write needs no If-Match (server-derived progress re-computes on repaint).

/** The asset-library kinds this editor handles (CHECK, minus the composed-only `other`).
 *  `file` is the generic Label/Asset combo (ART-201, Ryan): a free-text label + any uploaded file, with no
 *  preset category — it is what the type-picker-free "+ Add asset" flow creates. */
export type AssetKind = "cover" | "lyrics" | "music_video" | "lyric_video" | "canvas" | "photo" | "file";

interface KindConfig {
  label: string;
  input: "image" | "video" | "url" | "text" | "file";
  accept?: string;
  prompt?: string;
  purpose: string;
}

const KIND_CONFIG: Record<AssetKind, KindConfig> = {
  cover: { label: "Cover artwork", input: "image", accept: "image/*", prompt: "Drop cover art or browse — square JPG/PNG (3000×3000)", purpose: "release-cover" },
  photo: { label: "Photo", input: "image", accept: "image/*", prompt: "Drop a photo or browse — JPG/PNG", purpose: "release-photo" },
  canvas: { label: "Spotify Canvas", input: "video", accept: "video/mp4,video/quicktime,image/*", prompt: "Drop a 9:16 loop (3–8s) or browse", purpose: "release-canvas" },
  lyrics: { label: "Lyrics", input: "text", purpose: "release-lyrics" },
  music_video: { label: "Music video", input: "url", purpose: "release-music-video" },
  lyric_video: { label: "Lyric video", input: "url", purpose: "release-lyric-video" },
  file: { label: "File", input: "file", accept: "*/*", prompt: "Drop a file or browse", purpose: "release-file" },
};

/** Photo roles for the Photos gallery per-photo dropdown (shot-10). */
const PHOTO_ROLES = ["Profile", "Press", "Live", "Promo", "Behind the scenes", "Other"];

export interface AssetEditorModalProps {
  releaseId: string;
  /** The release id, seeding the fallback gradient in the existing-media preview. */
  seed?: string | null;
  /** A fixed kind (a tile-specific edit / add-photo / add-canvas), or undefined → show a kind picker (+Add Asset). */
  kind?: AssetKind;
  /** The asset being edited, or undefined → adding new. */
  asset?: AssetDto;
  onClose: () => void;
}

export function AssetEditorModal({ releaseId, seed, kind: fixedKind, asset, onClose }: AssetEditorModalProps) {
  const { uploadMedia } = useUploadMedia();
  // silent: the modal renders its own inline error (role="alert"), so suppress the hooks' global toast.
  const create = useCreateAsset(releaseId, { silent: true });
  const update = useUpdateAsset(releaseId, { silent: true });
  const remove = useDeleteAsset(releaseId, { silent: true });

  const editing = Boolean(asset?.assetId);
  // The type-picker-free "+ Add asset" flow (no fixedKind, not editing) is now the generic Label/Asset combo,
  // so it defaults to `file` rather than to a preset category (Ryan, ART-201).
  const [kind] = useState<AssetKind>(fixedKind ?? (asset?.kind as AssetKind) ?? "file");
  const cfg = KIND_CONFIG[kind];

  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState(asset?.url ?? "");
  const [text, setText] = useState(asset?.textContent ?? "");
  const [label, setLabel] = useState(asset?.label ?? "");
  const [photoRole, setPhotoRole] = useState(asset?.photoRole ?? PHOTO_ROLES[0]);

  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Abort any in-flight upload if the modal is force-unmounted (parent navigation), so no bytes keep flowing to
  // a dialog the user has left. During a save the dialog is otherwise LOCKED (handleClose no-ops) — no phantom
  // completion behind the user's back (the disabled-Cancel/live-Escape contradiction the review flagged).
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  function handleClose() {
    if (saving) return;
    onClose();
  }

  async function save() {
    setError(null);
    setSaving(true);
    abortRef.current = new AbortController();
    try {
      let mediaId = asset?.mediaId ?? null;

      // Upload kinds: if a new file was picked, run the flow now — its failure throws BEFORE we persist.
      const isUpload = cfg.input === "image" || cfg.input === "video" || cfg.input === "file";
      if (isUpload && file) {
        setProgress(0);
        const media = await uploadMedia(file, {
          kind: mediaKindForFile(file),
          purpose: cfg.purpose,
          onProgress: setProgress,
          signal: abortRef.current.signal,
        });
        mediaId = media.mediaId ?? null;
      }

      const trimmedUrl = url.trim();
      const trimmedText = text.trim();
      const hasContent =
        cfg.input === "url" ? Boolean(trimmedUrl) : cfg.input === "text" ? Boolean(trimmedText) : Boolean(mediaId);

      const body: components["schemas"]["AssetUpsertRequest"] = {
        kind,
        status: hasContent ? "added" : "missing",
        url: cfg.input === "url" ? (trimmedUrl || null) : null,
        mediaId: isUpload ? mediaId : null,
        textContent: cfg.input === "text" ? (trimmedText || null) : null,
        photoRole: kind === "photo" ? photoRole : null,
        // Caption is no longer set at add time (Ryan, ART-201 — "kill Add Caption"). Preserve any existing
        // caption on an edit so a save here never wipes one set elsewhere.
        caption: kind === "photo" ? (asset?.caption ?? null) : null,
        // The generic `file` asset's free-text label (the Label/Asset combo); ignored server-side for other kinds.
        label: kind === "file" ? (label.trim() || null) : null,
        position: asset?.position ?? null,
      };

      if (asset?.assetId) await update.mutateAsync({ assetId: asset.assetId, body });
      else await create.mutateAsync(body);
      onClose();
    } catch (e) {
      setError(errorMessage(e, "Couldn't save the asset"));
    } finally {
      setSaving(false);
      setProgress(undefined);
    }
  }

  function onDelete() {
    if (!asset?.assetId) return;
    setError(null);
    remove.mutate(asset.assetId, { onSuccess: onClose, onError: (e) => setError(errorMessage(e, "Couldn't remove the asset")) });
  }

  // A fixed-kind add (from a tile / "Add photos") names the kind — "Add photo", not the generic "Add asset"
  // (Ryan, ART-201). Only the type-picker add (no fixedKind) stays generic. The title and the primary button
  // share the label so they can't disagree.
  const addLabel = fixedKind ? `Add ${cfg.label.toLowerCase()}` : "Add asset";
  const title = editing ? `Edit ${cfg.label.toLowerCase()}` : addLabel;
  return (
    <Modal title={title} onClose={handleClose}>
      <div className={styles.body}>
        {/* The generic Label/Asset combo (Ryan, ART-201): a free-text label + a file, no preset category. It
            replaces the old "Asset type" preset dropdown — the structured kinds (cover/lyrics/videos/canvas/
            photo) keep their own tiles; this flow is any named deliverable. */}
        {kind === "file" && (
          <Field
            label="Label"
            placeholder="Name this asset (e.g. Instrumental, Split sheet, Alt cover)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={saving}
          />
        )}

        {(cfg.input === "image" || cfg.input === "video" || cfg.input === "file") && (
          <UploadField
            accept={cfg.accept!}
            file={file}
            onFile={setFile}
            progress={progress}
            prompt={cfg.prompt!}
            disabled={saving}
            existing={
              asset?.mediaId && cfg.input !== "file" ? (
                <MediaImage mediaId={asset.mediaId} url={asset.mediaUrl} seed={seed} alt="" className={styles.existingImg} lazy={false} />
              ) : undefined
            }
          />
        )}

        {cfg.input === "url" && (
          <Field
            label={`${cfg.label} URL`}
            placeholder="youtu.be/your-video"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={saving}
          />
        )}

        {cfg.input === "text" && (
          <label className={styles.field}>
            <span className={styles.label}>Lyrics</span>
            <textarea
              className={styles.textarea}
              rows={10}
              placeholder="Paste the lyrics…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={saving}
            />
          </label>
        )}

        {kind === "photo" && (
          <div className={styles.photoMeta}>
            <Picker
              label="Role"
              className={styles.field}
              value={photoRole}
              disabled={saving}
              onChange={setPhotoRole}
              options={PHOTO_ROLES.map((r) => ({ value: r, label: r }))}
            />
          </div>
        )}

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          {editing && (
            <Button variant="ghost" size="sm" onClick={onDelete} loading={remove.isPending} disabled={saving}>
              Remove
            </Button>
          )}
          <div className={styles.actionsRight}>
            <DialogCancel size="sm" disabled={saving} />
            <Button size="sm" onClick={save} loading={saving}>
              {editing ? "Save" : addLabel}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
